import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/utils/formatters";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  MagnifyingGlass, SignIn, SignOut, ArrowClockwise,
  UsersFour, Truck, Van, FileText, ArrowRight, UserPlus
} from "@phosphor-icons/react";
import { Label } from "@/components/ui/label";

const CATEGORY_ICONS = { CREW: UsersFour, TRUCK: Truck, VAN: Van, FACILITY: FileText, CRAFT: FileText, WALKIN: UserPlus, OTHER: FileText };
const CATEGORY_COLORS = {
  CREW: "bg-[#007AFF]/20 text-[#007AFF] border-[#007AFF]/40",
  TRUCK: "bg-[#FACC15]/20 text-[#FACC15] border-[#FACC15]/40",
  VAN: "bg-[#32D74B]/20 text-[#32D74B] border-[#32D74B]/40",
  FACILITY: "bg-[#A855F7]/20 text-[#A855F7] border-[#A855F7]/40",
  CRAFT: "bg-[#EC4899]/20 text-[#EC4899] border-[#EC4899]/40",
  WALKIN: "bg-[#FF9500]/20 text-[#FF9500] border-[#FF9500]/40",
  OTHER: "bg-[#262626] text-[#A3A3A3] border-[#262626]",
};

function StatusBadge({ status }) {
  if (status === "in") return <Badge className="bg-[#32D74B]/20 text-[#32D74B] border border-[#32D74B]/40 rounded-sm text-[10px] uppercase tracking-wider">IN</Badge>;
  if (status === "out") return <Badge className="bg-[#FF3B30]/20 text-[#FF3B30] border border-[#FF3B30]/40 rounded-sm text-[10px] uppercase tracking-wider">OUT</Badge>;
  return <Badge className="bg-[#262626] text-[#737373] border-0 rounded-sm text-[10px] uppercase tracking-wider">—</Badge>;
}

function CategoryBadge({ category }) {
  const Icon = CATEGORY_ICONS[category] || FileText;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider border ${CATEGORY_COLORS[category] || CATEGORY_COLORS.OTHER}`}>
      <Icon size={11} weight="bold" /> {category}
    </span>
  );
}

function RecentEventRow({ event }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-[#1A1A1A] last:border-0">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <CategoryBadge category={event.document_category} />
        <span className="text-sm text-white truncate">{event.record_name || event.record_carplate || "\u2014"}</span>
      </div>
      <div className="flex flex-col items-end shrink-0">
        <span className="text-[10px] font-mono-data text-[#A3A3A3]">{new Date(event.timestamp).toLocaleTimeString()}</span>
        <span className="text-[9px] text-[#737373] uppercase tracking-wider">by {event.by_username || event.by_user_name}</span>
      </div>
    </div>
  );
}

export function SecurityTab() {
  const [dashboard, setDashboard] = useState(null);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [selectedType, setSelectedType] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [kpiView, setKpiView] = useState(null); // "total_in" | "total_out" | "currently_in" | "currently_out"
  const [kpiData, setKpiData] = useState(null);
  const [kpiLoading, setKpiLoading] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [walkinForm, setWalkinForm] = useState({ name: "", position: "", nric_passport: "" });
  const [registering, setRegistering] = useState(false);
  const [showDocumentsLoaded, setShowDocumentsLoaded] = useState(() => {
    const saved = localStorage.getItem("sa_show_documents_loaded");
    return saved === null ? true : saved === "true";
  });

  useEffect(() => {
    const sync = () => {
      const saved = localStorage.getItem("sa_show_documents_loaded");
      setShowDocumentsLoaded(saved === null ? true : saved === "true");
    };
    window.addEventListener("storage", sync);
    window.addEventListener("sa-documents-loaded-visibility", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("sa-documents-loaded-visibility", sync);
    };
  }, []);

  const openKpi = async (view) => {
    setKpiView(view);
    setKpiLoading(true);
    setKpiData(null);
    try {
      const { data } = await api.get(`/security/list?view=${view}&limit=500`);
      setKpiData(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load list");
    } finally {
      setKpiLoading(false);
    }
  };

  const loadDashboard = useCallback(async () => {
    try {
      const { data } = await api.get("/security/dashboard");
      setDashboard(data);
    } catch (err) {
      console.error("dashboard load failed:", err);
    }
  }, []);

  const loadDocumentTypes = useCallback(async () => {
    try {
      const { data } = await api.get("/security/document-types");
      setDocumentTypes(data.types || []);
    } catch (err) {
      console.error("doc-types load failed:", err);
    }
  }, []);

  const runSearch = useCallback(async (query = "", docType = "") => {
    setSearching(true);
    try {
      const params = new URLSearchParams();
      if (query.trim()) params.set("search", query.trim());
      if (docType) params.set("document_type", docType);
      params.set("limit", "200");
      const { data } = await api.get(`/security/records?${params.toString()}`);
      setSearchResults(data.records || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Search failed");
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleRegisterWalkIn = async () => {
    const name = walkinForm.name.trim();
    if (!name) { toast.error("Name is required"); return; }
    setRegistering(true);
    try {
      const { data } = await api.post("/security/walkin", {
        name,
        position: walkinForm.position.trim(),
        nric_passport: walkinForm.nric_passport.trim(),
      });
      toast.success(`Registered No.${data.row_no} — ${data.name}`);
      setShowRegister(false);
      setWalkinForm({ name: "", position: "", nric_passport: "" });
      await Promise.all([loadDashboard(), loadDocumentTypes(), runSearch(searchQuery, selectedType)]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Registration failed");
    } finally {
      setRegistering(false);
    }
  };

  useEffect(() => {
    loadDashboard();
    loadDocumentTypes();
    runSearch("", "");
  }, [loadDashboard, loadDocumentTypes, runSearch]);

  const doAction = async (record, action) => {
    setActionLoading((s) => ({ ...s, [record._id]: action }));
    try {
      const { data } = await api.post(`/security/records/${record._id}/${action}`, {});
      toast.success(`${action === "check-in" ? "Checked IN" : "Checked OUT"}: ${record.name || record.carplate}`);
      setSearchResults((rs) => rs.map((r) => (r._id === record._id ? { ...r, ...data, _id: record._id } : r)));
      await loadDashboard();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || `${action} failed`);
    } finally {
      setActionLoading((s) => { const c = { ...s }; delete c[record._id]; return c; });
    }
  };

  // Live search — debounce
  useEffect(() => {
    const t = setTimeout(() => runSearch(searchQuery, selectedType), 300);
    return () => clearTimeout(t);
  }, [searchQuery, selectedType, runSearch]);

  return (
    <div className="space-y-6 max-w-7xl">
      {/* Dashboard KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <button onClick={() => openKpi("total_in")} data-testid="kpi-total-in"
          className="bg-[#121212] border border-[#262626] rounded-sm p-4 text-left hover:border-[#FF9500]/60 hover:bg-[#0F0F0F] transition group">
          <div className="flex items-center justify-between mb-1">
            <p className="text-[10px] uppercase tracking-[0.15em] text-[#737373]">Total IN</p>
            <ArrowRight size={12} weight="bold" className="text-[#737373] group-hover:text-[#FF9500]" />
          </div>
          <p className="text-4xl font-heading font-bold text-[#FF9500] font-mono-data" data-testid="security-total-in">{dashboard?.total_in ?? "—"}</p>
          <p className="text-[10px] text-[#737373] mt-1">All check-in events</p>
        </button>

        <button onClick={() => openKpi("currently_in")} data-testid="kpi-currently-in"
          className="bg-black border border-[#262626] rounded-sm p-4 text-left hover:border-[#32D74B]/60 hover:bg-[#0A0A0A] transition group animate-blink-alert">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3] font-bold">Currently IN</p>
            <ArrowRight size={12} weight="bold" className="text-[#737373] group-hover:text-[#32D74B]" />
          </div>
          <p className="text-4xl font-heading font-bold text-[#32D74B] font-mono-data" data-testid="security-currently-in">{dashboard?.currently_in ?? "—"}</p>
          <p className="text-xs text-[#A3A3A3] mt-1 font-bold uppercase tracking-wider">On-site right now</p>
        </button>

        <button onClick={() => openKpi("total_out")} data-testid="kpi-total-out"
          className="bg-[#121212] border border-[#262626] rounded-sm p-4 text-left hover:border-[#FF9500]/60 hover:bg-[#0F0F0F] transition group">
          <div className="flex items-center justify-between mb-1">
            <p className="text-[10px] uppercase tracking-[0.15em] text-[#737373]">Total OUT</p>
            <ArrowRight size={12} weight="bold" className="text-[#737373] group-hover:text-[#FF9500]" />
          </div>
          <p className="text-4xl font-heading font-bold text-[#FF9500] font-mono-data" data-testid="security-total-out">{dashboard?.total_out ?? "—"}</p>
          <p className="text-[10px] text-[#737373] mt-1">All check-out events</p>
        </button>

        <button onClick={() => openKpi("currently_out")} data-testid="kpi-currently-out"
          className="bg-black border border-[#262626] rounded-sm p-4 text-left hover:border-[#FF3B30]/60 hover:bg-[#0A0A0A] transition group animate-blink-alert">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3] font-bold">Currently OUT</p>
            <ArrowRight size={12} weight="bold" className="text-[#737373] group-hover:text-[#FF3B30]" />
          </div>
          <p className="text-4xl font-heading font-bold text-[#FF3B30] font-mono-data" data-testid="security-currently-out">{dashboard?.currently_out ?? "—"}</p>
          <p className="text-xs text-[#A3A3A3] mt-1 font-bold uppercase tracking-wider">Last known state = out</p>
        </button>
      </div>

      {/* Documents loaded chips */}
      {showDocumentsLoaded && (
      <Card className="bg-[#121212] border-[#262626] rounded-sm shadow-none">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] uppercase tracking-[0.15em] text-[#737373]">Documents loaded</p>
            <Button size="sm" variant="ghost" onClick={() => { loadDashboard(); loadDocumentTypes(); }} className="text-[#737373] hover:text-white h-7">
              <ArrowClockwise size={12} className="mr-1" /> <span className="text-[10px]">Refresh</span>
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {documentTypes.length === 0 ? (
              <p className="text-xs text-[#737373]">No documents uploaded yet.</p>
            ) : documentTypes.map((t) => (
              <button
                key={t.document_type}
                onClick={() => { setSelectedType(selectedType === t.document_type ? "" : t.document_type); }}
                className={`px-2 py-1 rounded-sm text-[10px] uppercase tracking-wider border transition ${
                  selectedType === t.document_type ? CATEGORY_COLORS[t.document_category] : "bg-[#0A0A0A] text-[#A3A3A3] border-[#262626] hover:border-[#404040]"
                }`}
                data-testid={`doc-type-${t.document_category}`}
              >
                {t.document_category} · {t.count}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
      )}

      {/* Recent Check-ins / Check-outs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card className="bg-[#121212] border-[#262626] rounded-sm shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-sm font-semibold text-[#32D74B] uppercase tracking-wider flex items-center gap-2">
              <SignIn size={14} weight="bold" /> Last 5 Check-Ins
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {(dashboard?.last_check_ins || []).length === 0 ? (
              <p className="text-xs text-[#737373]">No check-ins yet.</p>
            ) : (
              dashboard.last_check_ins.map((ev) => <RecentEventRow key={ev._id} event={ev} />)
            )}
          </CardContent>
        </Card>
        <Card className="bg-[#121212] border-[#262626] rounded-sm shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-sm font-semibold text-[#FF3B30] uppercase tracking-wider flex items-center gap-2">
              <SignOut size={14} weight="bold" /> Last 5 Check-Outs
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {(dashboard?.last_check_outs || []).length === 0 ? (
              <p className="text-xs text-[#737373]">No check-outs yet.</p>
            ) : (
              dashboard.last_check_outs.map((ev) => <RecentEventRow key={ev._id} event={ev} />)
            )}
          </CardContent>
        </Card>
      </div>

      {/* Search + Table */}
      <Card className="bg-[#121212] border-[#262626] rounded-sm shadow-none">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3 flex-wrap">
            <CardTitle className="font-heading text-sm font-semibold text-white uppercase tracking-wider flex items-center gap-2">
              <MagnifyingGlass size={16} weight="bold" className="text-[#007AFF]" /> Search &amp; Check-In/Out
            </CardTitle>
            <div className="flex-1 min-w-[200px] flex items-center gap-2">
              <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by NO, name, NRIC/passport, or contact number..."
                className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm font-mono-data" data-testid="security-search-input" />
              <Select value={selectedType || "__all__"} onValueChange={(v) => setSelectedType(v === "__all__" ? "" : v)}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm w-[220px]" data-testid="security-doc-filter">
                  <SelectValue placeholder="All document types" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-[#262626] text-white">
                  <SelectItem value="__all__">All document types</SelectItem>
                  {documentTypes.map((t) => (
                    <SelectItem key={t.document_type} value={t.document_type}>{t.document_category} · {t.count}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={() => setShowRegister(true)}
                className="bg-[#FF9500] hover:bg-[#FF9500]/80 text-black rounded-sm text-xs font-semibold whitespace-nowrap"
                data-testid="security-register-walkin-btn">
                <UserPlus size={14} className="mr-1" weight="bold" /> Register
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="border border-[#262626] rounded-sm overflow-auto max-h-[60vh]">
            <Table>
              <TableHeader className="sticky top-0 bg-[#0A0A0A] z-10">
                <TableRow className="border-b border-[#262626] hover:bg-transparent">
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] text-[#737373] w-16">NO/Code</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] text-[#737373]">Name / Driver</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] text-[#737373]">Category</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] text-[#737373]">Position / Dept</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] text-[#737373]">NRIC / Passport</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] text-[#737373]">Carplate</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] text-[#737373]">Contact</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] text-[#737373] text-center">Status</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] text-[#737373] text-center w-56">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {searching ? (
                  <TableRow><TableCell colSpan={9} className="text-center text-sm text-[#737373] py-6">Searching…</TableCell></TableRow>
                ) : searchResults.length === 0 ? (
                  <TableRow><TableCell colSpan={9} className="text-center text-sm text-[#737373] py-6">
                    {searchQuery || selectedType ? "No matches found." : "Upload a PDF to get started."}
                  </TableCell></TableRow>
                ) : searchResults.map((r) => (
                  <TableRow key={r._id} className="border-b border-[#1A1A1A] hover:bg-[#1A1A1A]" data-testid={`security-row-${r._id}`}>
                    <TableCell className="font-mono-data text-xs text-[#A3A3A3]">{r.row_no || r.code || "—"}</TableCell>
                    <TableCell className="text-sm text-white">{r.name || "\u2014"}</TableCell>
                    <TableCell><CategoryBadge category={r.document_category} /></TableCell>
                    <TableCell className="text-xs text-[#A3A3A3]">{r.position || "\u2014"}</TableCell>
                    <TableCell className="font-mono-data text-xs text-[#A3A3A3]">{r.nric_passport || "\u2014"}</TableCell>
                    <TableCell className="font-mono-data text-xs font-bold text-white">{r.carplate || "\u2014"}</TableCell>
                    <TableCell className="font-mono-data text-xs text-[#A3A3A3]">{r.contact_number || "\u2014"}</TableCell>
                    <TableCell className="text-center"><StatusBadge status={r.current_status} /></TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button size="sm" onClick={() => doAction(r, "check-in")}
                          disabled={!!actionLoading[r._id]}
                          className="bg-[#32D74B]/20 hover:bg-[#32D74B]/40 text-[#32D74B] border border-[#32D74B]/40 rounded-sm text-[10px] h-7 px-2"
                          data-testid={`check-in-${r._id}`}>
                          <SignIn size={11} weight="bold" className="mr-0.5" /> IN {r.check_in_count ? `(${r.check_in_count})` : ""}
                        </Button>
                        <Button size="sm" onClick={() => doAction(r, "check-out")}
                          disabled={!!actionLoading[r._id]}
                          className="bg-[#FF3B30]/20 hover:bg-[#FF3B30]/40 text-[#FF3B30] border border-[#FF3B30]/40 rounded-sm text-[10px] h-7 px-2"
                          data-testid={`check-out-${r._id}`}>
                          <SignOut size={11} weight="bold" className="mr-0.5" /> OUT {r.check_out_count ? `(${r.check_out_count})` : ""}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <p className="text-[10px] text-[#737373] mt-2">{searchResults.length} record(s) shown. Each Check-In/Out click increments the counter and updates the dashboard.</p>
        </CardContent>
      </Card>

      {/* KPI List Dialog */}
      <Dialog open={!!kpiView} onOpenChange={(open) => { if (!open) { setKpiView(null); setKpiData(null); } }}>
        <DialogContent className="bg-[#121212] border-[#262626] rounded-sm max-w-4xl" data-testid="kpi-dialog">
          <DialogHeader>
            <DialogTitle className={`font-heading text-lg uppercase tracking-wider flex items-center gap-2 ${kpiView && kpiView.includes("out") ? "text-[#FF3B30]" : "text-[#32D74B]"}`}>
              {kpiView === "total_in" && <><SignIn size={18} weight="bold" /> TOTAL IN — ALL CHECK-IN EVENTS</>}
              {kpiView === "total_out" && <><SignOut size={18} weight="bold" /> TOTAL OUT — ALL CHECK-OUT EVENTS</>}
              {kpiView === "currently_in" && <><SignIn size={18} weight="bold" /> CURRENTLY IN — ON-SITE NOW</>}
              {kpiView === "currently_out" && <><SignOut size={18} weight="bold" /> CURRENTLY OUT</>}
            </DialogTitle>
          </DialogHeader>
          {kpiLoading ? (
            <p className="text-sm text-[#737373] py-6 text-center">Loading…</p>
          ) : !kpiData || kpiData.count === 0 ? (
            <p className="text-sm text-[#737373] py-6 text-center">No entries.</p>
          ) : kpiData.kind === "events" ? (
            <div className="border border-[#262626] rounded-sm overflow-auto max-h-[65vh]">
              <Table>
                <TableHeader className="sticky top-0 bg-[#0A0A0A] z-10">
                  <TableRow className="border-b border-[#262626] hover:bg-transparent">
                    <TableHead className="text-[10px] uppercase tracking-[0.15em] text-[#737373]">Time</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.15em] text-[#737373]">Category</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.15em] text-[#737373]">Name / Driver</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.15em] text-[#737373]">Carplate</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.15em] text-[#737373]">By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {kpiData.items.map((ev) => (
                    <TableRow key={ev._id} className="border-b border-[#1A1A1A] hover:bg-[#1A1A1A]" data-testid={`kpi-event-${ev._id}`}>
                      <TableCell className="font-mono-data text-xs text-[#A3A3A3] whitespace-nowrap">{new Date(ev.timestamp).toLocaleString()}</TableCell>
                      <TableCell><CategoryBadge category={ev.document_category} /></TableCell>
                      <TableCell className="text-sm text-white">{ev.record_name || "\u2014"}</TableCell>
                      <TableCell className="font-mono-data text-xs text-white">{ev.record_carplate || "\u2014"}</TableCell>
                      <TableCell className="text-xs text-[#A3A3A3] uppercase tracking-wider">{ev.by_username || ev.by_user_name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="border border-[#262626] rounded-sm overflow-auto max-h-[65vh]">
              <Table>
                <TableHeader className="sticky top-0 bg-[#0A0A0A] z-10">
                  <TableRow className="border-b border-[#262626] hover:bg-transparent">
                    <TableHead className="text-[10px] uppercase tracking-[0.15em] text-[#737373]">Category</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.15em] text-[#737373]">Name / Driver</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.15em] text-[#737373]">Position / Dept</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.15em] text-[#737373]">Carplate</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.15em] text-[#737373]">Last activity</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.15em] text-[#737373] text-right">Counts</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {kpiData.items.map((r) => (
                    <TableRow key={r._id} className="border-b border-[#1A1A1A] hover:bg-[#1A1A1A]" data-testid={`kpi-record-${r._id}`}>
                      <TableCell><CategoryBadge category={r.document_category} /></TableCell>
                      <TableCell className="text-sm text-white">{r.name || "\u2014"}</TableCell>
                      <TableCell className="text-xs text-[#A3A3A3]">{r.position || "\u2014"}</TableCell>
                      <TableCell className="font-mono-data text-xs text-white">{r.carplate || "\u2014"}</TableCell>
                      <TableCell className="font-mono-data text-[10px] text-[#A3A3A3] whitespace-nowrap">
                        {kpiView === "currently_in" ? (r.last_check_in_at ? new Date(r.last_check_in_at).toLocaleString() : "\u2014") : (r.last_check_out_at ? new Date(r.last_check_out_at).toLocaleString() : "\u2014")}
                      </TableCell>
                      <TableCell className="text-right font-mono-data text-xs">
                        <span className="text-[#32D74B]">IN {r.check_in_count || 0}</span>
                        <span className="text-[#737373]"> · </span>
                        <span className="text-[#FF3B30]">OUT {r.check_out_count || 0}</span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <p className="text-[10px] text-[#737373]">{kpiData?.count || 0} entry(ies)</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setKpiView(null); setKpiData(null); }} className="border-[#262626] text-[#A3A3A3] hover:bg-[#1A1A1A] hover:text-white rounded-sm">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Register Walk-In Dialog */}
      <Dialog open={showRegister} onOpenChange={(open) => { if (!open) { setShowRegister(false); setWalkinForm({ name: "", position: "", nric_passport: "" }); } }}>
        <DialogContent className="bg-[#121212] border-[#262626] rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-white flex items-center gap-2">
              <UserPlus size={20} weight="bold" className="text-[#FF9500]" /> REGISTER WALK-IN
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-[#737373]">
              A unique <span className="text-white font-semibold">No.</span> starting from <span className="font-mono-data text-[#FF9500]">200</span> is auto-assigned. The record will be saved to the walk-in database and appear in Security search.
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">Name <span className="text-[#FF3B30]">*</span></Label>
              <Input value={walkinForm.name} onChange={(e) => setWalkinForm({ ...walkinForm, name: e.target.value })}
                placeholder="Full name"
                className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm" data-testid="walkin-name-input" autoFocus />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">Position</Label>
              <Input value={walkinForm.position} onChange={(e) => setWalkinForm({ ...walkinForm, position: e.target.value })}
                placeholder="e.g. Contractor, Supplier"
                className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm" data-testid="walkin-position-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">NRIC / Passport No</Label>
              <Input value={walkinForm.nric_passport} onChange={(e) => setWalkinForm({ ...walkinForm, nric_passport: e.target.value })}
                placeholder="ID number"
                className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm font-mono-data" data-testid="walkin-nric-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRegister(false)}
              className="border-[#262626] text-[#A3A3A3] hover:bg-[#1A1A1A] hover:text-white rounded-sm"
              data-testid="walkin-cancel-btn">
              Cancel
            </Button>
            <Button onClick={handleRegisterWalkIn} disabled={registering || !walkinForm.name.trim()}
              className="bg-[#FF9500] hover:bg-[#FF9500]/80 text-black rounded-sm font-semibold"
              data-testid="walkin-save-btn">
              {registering ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
